import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Leaf, BookOpen, Calculator, PenTool, Search, ArrowLeft, Calendar, ChevronRight, ChevronDown, Download, Lock, Sparkles, Check } from 'lucide-react';
import BrandMark from '@/components/BrandMark';
import { isUnlocked } from '@/utils/entitlements';
import CurriculumLevelNote from '@/components/CurriculumLevelNote';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUBJECTS = ['maths', 'english'];

const subjectIcon = (s) => s === 'maths' ? <Calculator className="w-4 h-4" /> : <PenTool className="w-4 h-4" />;
const subjectColor = (s) => s === 'maths' ? 'bg-moana-50 text-moana' : 'bg-fern-50 text-fern';

/** Group a flat lesson array into { [year]: { [term]: { [week]: { maths: [], english: [], total, completed } } } }. */
function groupLessons(lessons, completedRefs) {
  const byYear = {};
  lessons.forEach(l => {
    const year = l.year || 5;
    const term = l.term;
    const week = l.week;
    byYear[year] = byYear[year] || {};
    byYear[year][term] = byYear[year][term] || {};
    byYear[year][term][week] = byYear[year][term][week] || { maths: [], english: [], total: 0, completed: 0 };
    byYear[year][term][week][l.subject].push(l);
    byYear[year][term][week].total += 1;
    if (completedRefs.has(l.reference)) byYear[year][term][week].completed += 1;
  });
  Object.values(byYear).forEach(terms => {
    Object.values(terms).forEach(weeks => {
      Object.values(weeks).forEach(w => {
        w.maths.sort((a, b) => a.day - b.day);
        w.english.sort((a, b) => a.day - b.day);
      });
    });
  });
  return byYear;
}

/** "{maths unit} · {english unit}" per the subject filter; null if neither exists. */
function weekUnitLabel(weekData, filter) {
  const mathsUnit = weekData.maths[0]?.unit || null;
  const englishUnit = weekData.english[0]?.unit || null;
  if (filter === 'maths') return mathsUnit;
  if (filter === 'english') return englishUnit;
  const parts = [mathsUnit, englishUnit].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

/** Mirrors billing.py's is_lesson_unlocked term/week logic (via the shared
 * isUnlocked helper), for display only — the backend is the source of truth
 * and enforces this server-side. `entitlements` should already be scoped to
 * the active child. */
function weekLockReason(weekData, entitlements, unlimitedAccess, year, term, week) {
  if (unlimitedAccess) return null;
  const subjectsInWeek = [...weekData.maths, ...weekData.english].map(l => l.subject);
  const uniqueSubjects = [...new Set(subjectsInWeek)];
  // day: 3 so this never short-circuits on the T1/W1 free-trial special case —
  // that's handled separately below via its own message.
  const anyUnlocked = uniqueSubjects.some(subject =>
    isUnlocked(entitlements, { subject, year, term, week, day: 3 }, unlimitedAccess)
  );
  if (anyUnlocked) return null;
  if (term === 1 && week === 1) {
    return 'Days 1-2 are free to try — unlock the rest of this week from Pricing.';
  }
  return 'Finish the previous week to unlock this one, or buy it directly from Pricing.';
}

function lessonMatchesSearch(l, query) {
  const q = query.toLowerCase();
  return l.title.toLowerCase().includes(q) || l.teReoTitle.toLowerCase().includes(q);
}

/** Compact single-lesson row used inside a week section. */
function LessonMiniCard({ lesson, done, unlocked, trial, onOpen, onDownload }) {
  return (
    <div
      data-testid={`lesson-card-${lesson.reference}`}
      onClick={onOpen}
      className={`flex items-center gap-3 p-3 rounded-2xl border cursor-pointer transition-all hover:shadow-sm ${done ? 'border-green-200 bg-green-50/40' : 'border-slate-100 bg-white'} ${!unlocked ? 'opacity-75' : ''}`}
    >
      <Badge className={`${subjectColor(lesson.subject)} border-0 rounded-full text-[10px] shrink-0`}>D{lesson.day}</Badge>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-fern-dark truncate">{lesson.title}</p>
        <p className="text-xs text-slate-400">{lesson.duration} min</p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {trial && (
          <Badge data-testid={`trial-${lesson.reference}`} className="bg-kowhai/30 text-fern-dark border-0 rounded-full text-[10px] gap-1 px-2 py-0.5">
            <Sparkles className="w-2.5 h-2.5" /> Free
          </Badge>
        )}
        {!unlocked && !trial && (
          <Badge data-testid={`locked-${lesson.reference}`} className="bg-slate-100 text-slate-500 border-0 rounded-full text-[10px] gap-1 px-2 py-0.5">
            <Lock className="w-2.5 h-2.5" /> Locked
          </Badge>
        )}
        {done && <Check className="w-4 h-4 text-green-600" />}
        <Button data-testid={`download-${lesson.reference}`} variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-400 hover:text-moana"
          onClick={(e) => { e.stopPropagation(); onDownload(); }}>
          <Download className="w-4 h-4" />
        </Button>
        <ChevronRight className="w-4 h-4 text-slate-300" />
      </div>
    </div>
  );
}

export default function LessonsPage() {
  const { authHeaders, childParam, activeChildName, user } = useAuth();
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [progress, setProgress] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [entitlements, setEntitlements] = useState([]);
  const [unlimitedAccess, setUnlimitedAccess] = useState(false);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [expandedByTermWeek, setExpandedByTermWeek] = useState({});
  const [initializedTerms, setInitializedTerms] = useState(new Set());

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/lessons`),
      axios.get(`${API}/progress`, { headers: authHeaders })
    ]).then(([l, p]) => {
      setLessons(l.data);
      setProgress(p.data);
    });
    axios.get(`${API}/billing/entitlements`, { headers: authHeaders }).then(r => {
      setEntitlements(r.data.entitlements || []);
      setUnlimitedAccess(!!r.data.unlimited_access);
    }).catch(() => {});
  }, [authHeaders]);

  const isLessonUnlocked = (lesson) => {
    const isFullUnlimited = unlimitedAccess || user?.unlimited_access;
    if (isFullUnlimited) return true;
    if (isFreeTrial(lesson)) return true;
    if (!activeChildName) return false;
    const childEntitlements = entitlements.filter(e => e.child_name === activeChildName);
    return isUnlocked(childEntitlements, lesson, isFullUnlimited);
  };

  const isFreeTrial = (lesson) => lesson.term === 1 && lesson.week === 1 && (lesson.day === 1 || lesson.day === 2);

  const completedRefs = useMemo(() => new Set(progress.filter(p => p.completed).map(p => p.lesson_reference)), [progress]);

  const byYear = useMemo(() => groupLessons(lessons, completedRefs), [lessons, completedRefs]);
  const years = useMemo(() => Object.keys(byYear).map(Number).sort((a, b) => a - b), [byYear]);
  const byTerm = useMemo(() => (selectedYear !== null ? byYear[selectedYear] || {} : {}), [selectedYear, byYear]);
  const terms = useMemo(() => Object.keys(byTerm).map(Number).sort((a, b) => a - b), [byTerm]);

  // Default selectedYear once data has loaded: the lowest year available.
  useEffect(() => {
    if (selectedYear !== null || years.length === 0) return;
    setSelectedYear(years[0]);
  }, [years, selectedYear]);

  // Default selectedTerm once a year is selected: the lowest term with an
  // incomplete week, else the lowest term available.
  useEffect(() => {
    if (selectedYear === null || terms.length === 0) return;
    const withIncomplete = terms.find(t => Object.values(byTerm[t]).some(w => w.completed < w.total));
    setSelectedTerm(withIncomplete !== undefined ? withIncomplete : terms[0]);
  }, [selectedYear, terms]); // eslint-disable-line react-hooks/exhaustive-deps

  // Default expand/collapse for a term the first time it's viewed: only
  // the lowest-numbered incomplete week starts expanded.
  useEffect(() => {
    if (selectedTerm === null || initializedTerms.has(selectedTerm)) return;
    const weeksForTerm = byTerm[selectedTerm] || {};
    const weekNumbers = Object.keys(weeksForTerm).map(Number).sort((a, b) => a - b);
    const firstIncomplete = weekNumbers.find(w => weeksForTerm[w].completed < weeksForTerm[w].total);
    const defaults = {};
    weekNumbers.forEach(w => { defaults[w] = w === firstIncomplete; });
    setExpandedByTermWeek(prev => ({ ...prev, [selectedTerm]: defaults }));
    setInitializedTerms(prev => new Set(prev).add(selectedTerm));
  }, [selectedTerm, byTerm, initializedTerms]);

  const toggleWeek = (term, week) => {
    setExpandedByTermWeek(prev => ({
      ...prev,
      [term]: { ...(prev[term] || {}), [week]: !(prev[term] || {})[week] }
    }));
  };

  const setAllWeeks = (term, value) => {
    const weekNumbers = Object.keys(byTerm[term] || {}).map(Number);
    const next = {};
    weekNumbers.forEach(w => { next[w] = value; });
    setExpandedByTermWeek(prev => ({ ...prev, [term]: next }));
  };

  const activeSubjects = filter === 'all' ? SUBJECTS : [filter];

  const lessonsForWeekSubject = (weekData, subject) => {
    let list = weekData[subject];
    if (search) list = list.filter(l => lessonMatchesSearch(l, search));
    return list;
  };

  const weekVisible = (weekData) => {
    if (!search) return true;
    return activeSubjects.some(s => lessonsForWeekSubject(weekData, s).length > 0);
  };

  const handleDownload = (ref) => {
    fetch(`${API}/lessons/${ref}/pdf${childParam}`, { headers: authHeaders })
      .then(res => res.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${ref}-worksheet.pdf`;
        a.click();
        URL.revokeObjectURL(a.href);
      });
  };

  const weeksForSelectedTerm = selectedTerm !== null ? byTerm[selectedTerm] || {} : {};
  const weekNumbers = Object.keys(weeksForSelectedTerm).map(Number).sort((a, b) => a - b).filter(w => weekVisible(weeksForSelectedTerm[w]));

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-white/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/dashboard" className="flex items-center">
            <BrandMark size={36} />
          </Link>
          <nav className="flex items-center gap-2">
            <Link to="/dashboard"><Button variant="ghost" className="text-slate-600 hover:text-fern rounded-lg gap-1"><ArrowLeft className="w-4 h-4" /> Dashboard</Button></Link>
            <Link to="/planner"><Button variant="ghost" className="text-slate-600 hover:text-fern rounded-lg gap-1"><Calendar className="w-4 h-4" /> <span className="hidden sm:inline">Planner</span></Button></Link>
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="mb-8 animate-fadeInUp stagger-1">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-fern-dark tracking-tight mb-2">
            Ngā Akoranga / Lessons
          </h1>
          <p className="text-slate-600">Browse and start your lessons</p>
        </div>

        {/* Year tabs (only shown once more than one year has content) */}
        {years.length > 1 && (
          <div className="flex items-center gap-2 mb-4 animate-fadeInUp stagger-2">
            {years.map(y => (
              <Button key={y} data-testid={`year-tab-${y}`} variant={selectedYear === y ? 'default' : 'outline'}
                onClick={() => setSelectedYear(y)}
                className={`rounded-full gap-2 ${selectedYear === y ? 'bg-moana text-white hover:bg-moana-light' : 'border-slate-200 text-slate-600 hover:bg-moana/5'}`}>
                Year {y}
              </Button>
            ))}
          </div>
        )}

        {/* Term tabs */}
        {terms.length > 0 && (
          <div className="flex items-center gap-2 mb-4 animate-fadeInUp stagger-2">
            {terms.map(t => (
              <Button key={t} data-testid={`term-tab-${t}`} variant={selectedTerm === t ? 'default' : 'outline'}
                onClick={() => setSelectedTerm(t)}
                className={`rounded-full gap-2 ${selectedTerm === t ? 'bg-fern text-white hover:bg-fern-light' : 'border-slate-200 text-slate-600 hover:bg-fern/5'}`}>
                Term {t}
              </Button>
            ))}
          </div>
        )}

        {/* Declares when this year/term reaches a level beyond its own year. */}
        <CurriculumLevelNote year={selectedYear} term={selectedTerm} />

        {/* Search and filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4 animate-fadeInUp stagger-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input data-testid="lesson-search" placeholder="Search lessons..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-10 rounded-xl bg-white border-slate-200" />
          </div>
          <div className="flex gap-2">
            {[
              { key: 'all', label: 'All', icon: BookOpen },
              { key: 'maths', label: 'Maths', icon: Calculator },
              { key: 'english', label: 'English', icon: PenTool },
            ].map(f => (
              <Button key={f.key} data-testid={`filter-${f.key}`} variant={filter === f.key ? 'default' : 'outline'}
                onClick={() => setFilter(f.key)}
                className={`rounded-full gap-2 ${filter === f.key ? 'bg-fern text-white hover:bg-fern-light' : 'border-slate-200 text-slate-600 hover:bg-fern/5'}`}>
                <f.icon className="w-4 h-4" /> {f.label}
              </Button>
            ))}
          </div>
        </div>

        {selectedTerm !== null && weekNumbers.length > 0 && (
          <div className="flex justify-end gap-2 mb-4">
            <Button data-testid="expand-all" variant="ghost" size="sm" className="text-xs text-slate-500 hover:text-fern"
              onClick={() => setAllWeeks(selectedTerm, true)}>Expand all</Button>
            <Button data-testid="collapse-all" variant="ghost" size="sm" className="text-xs text-slate-500 hover:text-fern"
              onClick={() => setAllWeeks(selectedTerm, false)}>Collapse all</Button>
          </div>
        )}

        {/* Week sections */}
        <div className="space-y-4">
          {weekNumbers.map(week => {
            const weekData = weeksForSelectedTerm[week];
            const isExpanded = search ? true : !!(expandedByTermWeek[selectedTerm] || {})[week];
            const unitLabel = weekUnitLabel(weekData, filter);
            const childEntitlementsForLock = activeChildName ? entitlements.filter(e => e.child_name === activeChildName) : [];
            const lockReason = weekLockReason(weekData, childEntitlementsForLock, unlimitedAccess || user?.unlimited_access, selectedYear, selectedTerm, week);
            return (
              <Card key={week} data-testid={`week-section-${week}`} className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-3xl overflow-hidden">
                <button
                  data-testid={`week-toggle-${week}`}
                  onClick={() => toggleWeek(selectedTerm, week)}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <div>
                    <h2 className="font-heading font-semibold text-fern-dark text-lg">Week {week}</h2>
                    {unitLabel && <p className="text-sm text-slate-500">{unitLabel}</p>}
                    {lockReason && <p className="text-xs text-slate-400 italic mt-0.5" data-testid={`week-lock-reason-${selectedTerm}-${week}`}>{lockReason}</p>}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <Badge className="bg-sand-dark text-slate-600 border-0 rounded-full text-xs">{weekData.completed}/{weekData.total} done</Badge>
                    {isExpanded ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
                  </div>
                </button>
                {isExpanded && (
                  <CardContent className="pt-0 pb-5 px-5">
                    <div className={`grid grid-cols-1 ${activeSubjects.length > 1 ? 'md:grid-cols-2' : ''} gap-4`}>
                      {activeSubjects.map(subject => {
                        const subjectLessons = lessonsForWeekSubject(weekData, subject);
                        if (subjectLessons.length === 0) return null;
                        return (
                          <div key={subject}>
                            <div className="flex items-center gap-1.5 mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                              {subjectIcon(subject)} {subject === 'maths' ? 'Maths' : 'English'}
                            </div>
                            <div className="space-y-2">
                              {subjectLessons.map(lesson => (
                                <LessonMiniCard
                                  key={lesson.reference}
                                  lesson={lesson}
                                  done={completedRefs.has(lesson.reference)}
                                  unlocked={isLessonUnlocked(lesson)}
                                  trial={isFreeTrial(lesson)}
                                  onOpen={() => navigate(`/lessons/${lesson.reference}`)}
                                  onDownload={() => handleDownload(lesson.reference)}
                                />
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>

        {selectedTerm !== null && weekNumbers.length === 0 && (
          <div className="text-center py-16 text-slate-400">
            <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg">No lessons found matching your search.</p>
          </div>
        )}
      </main>
    </div>
  );
}
